import React, { useEffect, useMemo, useRef } from 'react';
import { gallerySources, type GalleryImage } from '../data/gallery';
import { useLanguage } from '../contexts/LanguageContext';
type ColumnProps = {
  images: GalleryImage[];
  direction: 'up' | 'down';
  duration: number;
};
function MarqueeColumn({
  images,
  direction,
  duration
}: ColumnProps) {
  const loop = [...images, ...images];
  return <div className="marquee-track" style={{
    animationName: direction === 'up' ? 'marquee-up' : 'marquee-down',
    animationDuration: `${duration}s`
  }} data-id="element-106" data-mp="1auoeavvye6ho">
      {loop.map((image, index) => <figure key={`${image.src}-${index}`} data-gallery-card className="group relative mb-5 overflow-hidden rounded-2xl border border-gold-deep/40 bg-ink-900 will-change-[opacity,transform]" data-id="element-107" data-mp="1jvml1lxs0s3y" data-mpi={index}>
          <img src={image.src} alt={index < images.length ? image.alt : ''} aria-hidden={index >= images.length} loading="lazy" className="aspect-[3/4] w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]" data-id="element-108" data-mp="1fwdpcbj6ek5r" />
          <span aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_top,rgba(7,6,5,0.72),transparent_58%)]" data-id="element-109" data-mp="239dvvtovf2mu" />
          <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-gold-soft/0 transition-[box-shadow] duration-300 ease-out group-hover:ring-gold-soft/40" data-id="element-110" data-mp="28bd0f6wck5ld" />
        </figure>)}
    </div>;
}
const FADE_ZONE = 0.13;
export function GalleryMarquee() {
  const {
    t
  } = useLanguage();
  const viewportRef = useRef<HTMLDivElement>(null);
  const images = useMemo<GalleryImage[]>(() => gallerySources.map((src, index) => ({
    src,
    alt: t.gallery.alts[index] ?? ''
  })), [t]);
  const columnA = images.filter((_, index) => index % 2 === 0);
  const columnB = images.filter((_, index) => index % 2 === 1);
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const cards = Array.from(viewport.querySelectorAll<HTMLElement>('[data-gallery-card]'));
    let frame = 0;
    const update = () => {
      const bounds = viewport.getBoundingClientRect();
      for (const card of cards) {
        const rect = card.getBoundingClientRect();
        const center = rect.top + rect.height / 2 - bounds.top;
        const position = center / bounds.height;
        const raw = Math.min(Math.max(Math.min(position, 1 - position) / FADE_ZONE, 0), 1);
        const eased = raw * raw * (3 - 2 * raw);
        card.style.opacity = `${0.12 + eased * 0.88}`;
        card.style.transform = `scale(${0.95 + eased * 0.05})`;
      }
      frame = window.requestAnimationFrame(update);
    };
    frame = window.requestAnimationFrame(update);
    return () => window.cancelAnimationFrame(frame);
  }, []);
  return <div className="relative" data-id="element-111" data-mp="1u7denlzwsx8e">
      <div className="pointer-events-none absolute -inset-8 rounded-[3rem] bg-[radial-gradient(circle_at_center,rgba(255,244,194,0.12),transparent_70%)]" aria-hidden="true" data-id="element-112" data-mp="3amobqdxp2wds" />

      <div ref={viewportRef} className="relative grid h-[540px] grid-cols-2 gap-5 overflow-hidden sm:h-[680px] lg:h-[780px]" data-id="element-113" data-mp="35py1464nsszr">
        <MarqueeColumn images={columnA} direction="up" duration={40} data-id="element-114" data-mp="3i67gkaa0lufx" />
        <MarqueeColumn images={columnB} direction="down" duration={48} data-id="element-115" data-mp="3i4cu2rj3hu9q" />
      </div>
    </div>;
}